/**
 * Pick the workspaces a maintenance notice goes to (#3561).
 *
 * A multi-select rather than a reuse of `CompanyFilter`: that one models "which
 * company am I looking at", where an empty value means *all* and picking is a
 * navigation. Here an empty value means nobody is written to, and every
 * selection is a decision about who receives real mail. The two look alike and
 * behave nothing alike.
 *
 * "Select all" applies to the FILTERED list, and says how many that is. A
 * select-all that silently means "all 400 workspaces" when the operator can see
 * six is how a test send reaches the whole customer base.
 */
import { useEffect, useMemo, useState } from "react";
import { Building2Icon, CheckIcon, ChevronsUpDownIcon, Loader2Icon, XIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@component/command";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";
import { getListWorkspaceCompany } from "@service/employer";
import { WorkSpaceCompanyItem } from "@type/company";
import { AdminWorkspaceCompanyOption } from "@type/admin";
import { catchErrorLog } from "@util/sentry";
import { companyTypeLabel } from "@util/company-type";
import { keepLiveOura, liveOuraCompanyIds } from "./oura-live-workspaces";

interface Props {
    value: string[];
    onChange: (companyIds: string[]) => void;
    disabled?: boolean;
    /** Refuse to grow past the server's per-send cap, rather than let the
     *  operator build a selection the request will reject. */
    max?: number;
}

export default function CompanyMultiSelect({ value, onChange, disabled, max }: Props) {
    const [open, setOpen] = useState(false);
    const [companies, setCompanies] = useState<AdminWorkspaceCompanyOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [query, setQuery] = useState("");

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        // Read together: an Oura workspace is offered only when a live business owns it (#4901).
        Promise.all([getListWorkspaceCompany({ page: 1, limit: 500 }), liveOuraCompanyIds()])
            .then(([resp, liveOura]) => {
                if (cancelled) return;
                const list = (resp?.data?.data ?? []) as WorkSpaceCompanyItem[];
                setCompanies(
                    keepLiveOura(
                        list
                            .filter((c) => c.company_id && c.company_name)
                            .map<AdminWorkspaceCompanyOption>((c) => ({
                                id: c.company_id,
                                name: c.company_name,
                                company_type: c.company_type,
                            })),
                        liveOura
                    )
                );
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(`Maintenance: failed to load workspace companies: ${err}`);
                setCompanies([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const byId = useMemo(
        () => new Map(companies.map((c) => [c.id, c])),
        [companies]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return companies;
        return companies.filter((c) => c.name.toLowerCase().includes(q));
    }, [companies, query]);

    const atCap = !!max && value.length >= max;

    const toggle = (id: string) => {
        if (value.includes(id)) {
            onChange(value.filter((v) => v !== id));
            return;
        }
        if (atCap) return;
        onChange([...value, id]);
    };

    const selectFiltered = () => {
        const merged = [...value];
        for (const c of filtered) {
            if (merged.includes(c.id)) continue;
            if (max && merged.length >= max) break;
            merged.push(c.id);
        }
        onChange(merged);
    };

    return (
        <div className="space-y-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className="w-full justify-between font-normal"
                    >
                        <span className="flex min-w-0 items-center gap-2">
                            <Building2Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span className="truncate">
                                {value.length === 0
                                    ? "Select workspaces"
                                    : `${value.length} workspace${value.length === 1 ? "" : "s"} selected`}
                            </span>
                        </span>
                        {isLoading ? (
                            <Loader2Icon className="h-4 w-4 flex-shrink-0 animate-spin text-gray-400" />
                        ) : (
                            <ChevronsUpDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search workspaces…"
                            value={query}
                            onValueChange={setQuery}
                        />
                        <CommandList>
                            <CommandEmpty>
                                {isLoading ? "Loading workspaces…" : "No workspace found."}
                            </CommandEmpty>
                            <CommandGroup>
                                {filtered.map((c) => {
                                    const selected = value.includes(c.id);
                                    return (
                                        <CommandItem
                                            key={c.id}
                                            value={c.id}
                                            onSelect={() => toggle(c.id)}
                                            disabled={!selected && atCap}
                                            className={cn(!selected && atCap && "opacity-50")}
                                        >
                                            <CheckIcon
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    selected ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            <span className="truncate">{c.name}</span>
                                            {/* Which product this workspace is on.
                                                Half these names are staging clones
                                                of each other, and "who am I about to
                                                email" is not answerable from the
                                                name alone. */}
                                            {c.company_type && (
                                                <span className="ml-2 inline-flex flex-shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                                    {companyTypeLabel(c.company_type)}
                                                </span>
                                            )}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                    <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-2 py-1.5">
                        {/* Names the count, always. "Select all" over a search box
                            is the one control that can quietly mean far more than
                            what is on screen. */}
                        <button
                            type="button"
                            onClick={selectFiltered}
                            disabled={filtered.length === 0 || atCap}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40"
                        >
                            Select these {filtered.length}
                        </button>
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            disabled={value.length === 0}
                            className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-40"
                        >
                            Clear
                        </button>
                    </div>
                </PopoverContent>
            </Popover>

            {value.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {value.map((id) => (
                        <span
                            key={id}
                            className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 py-1 pl-2.5 pr-1 text-xs text-gray-700"
                        >
                            <span className="truncate">{byId.get(id)?.name ?? id}</span>
                            {byId.get(id)?.company_type && (
                                <span className="flex-shrink-0 text-[10px] text-gray-500">
                                    {companyTypeLabel(byId.get(id)?.company_type)}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={() => toggle(id)}
                                disabled={disabled}
                                aria-label={`Remove ${byId.get(id)?.name ?? id}`}
                                className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                            >
                                <XIcon className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {atCap && (
                <p className="my-0 text-xs text-amber-700">
                    {max} workspaces is the most one send can cover. Send the rest in a second batch.
                </p>
            )}
        </div>
    );
}
